import { AfterViewInit, Component, ElementRef, OnInit, inject } from '@angular/core';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { CompaniesHouseService } from '@app/finxone-web-frontend/app/lib/services/companies-house-service/companies-house.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
  GenericHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy, UUID } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import {
  BaseCssConfig,
  baseCssConfigStyle,
  BaseCssProperties,
  BaseWidgetProperties,
  CompaniesHouseAddress,
  CompaniesHouseNumberResult,
  CompaniesHouseSearchResult,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { SafeHtmlPipe } from '../../../../lib/pipes/SafeHtml.pipe';
import { ListItemComponent } from '../../components-library/listing-components/list-item/list-item.component';

type CompanyResult = CompaniesHouseSearchResult | CompaniesHouseNumberResult;

@Component({
  selector: 'finxone-web-frontend-companies-house-listing-widget',
  templateUrl: './companies-house-listing-widget.component.html',
  imports: [SafeHtmlPipe, ListItemComponent],
})
export class CompaniesHouseListingWidgetComponent implements OnInit, AfterViewInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public companies: CompanyResult[] = [];
  public isApiInProgress = false;
  public baseCssConfig: BaseCssConfig;

  public noResultsTemplateFunction: HandlebarsTemplateDelegate<GenericHandleBarModel>;
  public fetchingTemplateFunction: HandlebarsTemplateDelegate<GenericHandleBarModel>;

  public renderedNoResultsTemplate = '';
  public renderedFetchingTemplate = '';

  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;

  public widgetProperties: BaseWidgetProperties;
  public contentStyle: baseCssConfigStyle;
  public containerHeight: string;

  public dataIn: ProviderClass<UiZoneWidgetAttributeConfig> = inject(ProviderClass);

  private readonly elementRef = inject(ElementRef);
  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly companiesHouseService = inject(CompaniesHouseService);
  private readonly navigationService = inject(NavigationService);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);

  ngAfterViewInit(): void {
    this.containerHeight = scrollLoading('companies-house-list', this.elementRef, 0, 0);
  }

  ngOnInit(): void {
    this.initStyles();
    this.initWidgetProperties();
    this.loadTemplates();
    this.loadSearchResults();
  }

  private initStyles(): void {
    this.baseCssConfig = this.dataIn.attri?.css_config;
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLORS_V2,
      BaseCssProperties.BORDER_V2,
      BaseCssProperties.BOX_SHADOW,
    ]);
  }

  private initWidgetProperties(): void {
    this.widgetProperties = this.dataIn?.attri?.widgetProperties ?? {};

    if (this.configService.getActiveConfigType().isWorkflow) {
      const appBuilderState = this.widgetProperties?.['appBuilderState'];
      if (appBuilderState === 'fetching') {
        this.appBuilderShowFetchingContent = true;
      } else if (appBuilderState === 'noRecords') {
        this.appBuilderShowNoRecordsContent = true;
      }
    }
  }

  private loadTemplates(): void {
    this.noResultsTemplateFunction = Handlebars.compile(this.widgetProperties?.['noResultsTemplate'] ?? '');
    this.fetchingTemplateFunction = Handlebars.compile(this.widgetProperties?.['fetchingTemplate'] ?? '');

    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedNoResultsTemplate = filterIfHandlebarNotRendered(this.noResultsTemplateFunction(model));
        this.renderedFetchingTemplate = filterIfHandlebarNotRendered(this.fetchingTemplateFunction(model));
      });
  }

  private loadSearchResults(): void {
    const formState = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.COMPANIES_HOUSE_SEARCH),
    );
    this.companies = formState?.formData?.['companyHouseSearchResult'] ?? [];
  }

  public onSelectCompany(company: CompanyResult): void {
    if (this.isApiInProgress) return;

    this.isApiInProgress = true;

    this.companiesHouseService
      .checkCompanyNumber(company.companyNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (companyDetails) => {
          this.dispatchSelectedCompany(companyDetails);
          this.navigate();
          this.isApiInProgress = false;
        },
        error: () => {
          this.isApiInProgress = false;
        },
      });
  }

  private mapCompanyAddressFields(address: CompaniesHouseAddress | undefined): Record<string, string> {
    return {
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      address3: address?.address3 ?? '',
      country: address?.country ?? '',
      state: address?.state ?? '',
      city: address?.city ?? '',
      postalCode: address?.postalCode ?? '',
      premises: address?.premises ?? '',
    };
  }

  private dispatchSelectedCompany(company: CompaniesHouseNumberResult): void {
    const { registeredOfficeAddress, serviceAddress, officers, ...companyDetails } = company;
    if (officers) {
      officers.items = officers.items?.map((officer) => ({
        ...officer,
        uniqueId: new UUID().toString(),
      }));
    }
    const companyCreationDate = companyDetails?.dateOfCreation
      ? new Date(companyDetails.dateOfCreation)
      : undefined;
    this.store.dispatch(
      new SetFormActionWithId(
        {
          type: FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW,
          formData: {
            ...companyDetails,
            companyAddress: { ...this.mapCompanyAddressFields(registeredOfficeAddress) },
            serviceAddress: { ...this.mapCompanyAddressFields(serviceAddress) },
            members: officers?.items,
            activeCount: officers?.activeCount ?? 0,
            resignedCount: officers?.resignedCount ?? 0,
            totalResults: officers?.totalResults ?? 0,
            companyCreationDay: companyCreationDate?.getDate(),
            companyCreationMonth: companyCreationDate?.getMonth(),
            companyCreationYear: companyCreationDate?.getFullYear(),
          },
        },
        FormActionTypeEnum.BUSINESS_ONBOARDING_FLOW,
      ),
    );
  }

  private navigate(): void {
    const attri = this.dataIn.attri;
    const pageId: string = attri?.['pageId'] ?? '';
    if (!pageId) return;

    const { pageUrl } = this.navigationService.buildPageNavigationUrl({
      pageId,
      zone: attri?.['zone'] ?? '',
      pageUrl: attri?.['pageUrl'] ?? '',
      role: attri?.['role'] ?? '',
      navigationOption: attri?.['navigationOption'],
      tabRef: attri?.['tabRef'] ?? '',
      pageTransition: attri?.['pageTransition'],
    });

    this.navigationService.navigateByPageUrl({
      url: pageUrl,
      targetRole: attri?.['role'] ?? '',
      currentRole: this.store.selectSnapshot(RoleState.getRole)?.role,
      zone: attri?.['zone'] ?? '',
      externalLink: attri?.['externalUrl'],
      browserBehaviour: attri?.['browserBehaviour'],
    });
  }
}
